import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { FOCUS_MODE, PARKED_PATHS } from './config/parkedFeatures';
import './App.css';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import ProtectedRoute from './components/ProtectedRoute';
import { GatedRoute } from './components/GatedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { Toaster } from './components/ui/toaster';
import { Toaster as SonnerToaster } from 'sonner';
import PlatformFooter from './components/PlatformFooter';
import GlobalSubheader from './components/GlobalSubheader';
import { EmailVerifyBanner } from './components/EmailVerifyBanner';
import ReviewerTour from './components/ReviewerTour';
import DemoRibbon from './components/DemoRibbon';
import ScrollToTop from './components/ScrollToTop';
import './i18n';

// Eager-loaded (critical path)
import NotaryLanding from './pages/NotaryLanding';
import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';

// Lazy-loaded pages
const HomePage = React.lazy(() => import('./pages/HomePage'));
const IntakePortal = React.lazy(() => import('./pages/IntakePortal'));
const ClosingRooms = React.lazy(() => import('./pages/ClosingRooms'));
const ClosingRoom = React.lazy(() => import('./pages/ClosingRoom'));
const ClosingRoomJoin = React.lazy(() => import('./pages/ClosingRoomJoin'));
const FederalAssets = React.lazy(() => import('./pages/FederalAssets'));
const FederalPortfolio = React.lazy(() => import('./pages/FederalPortfolio'));
const FederalVerify = React.lazy(() => import('./pages/FederalVerify'));
const TimestampVerify = React.lazy(() => import('./pages/TimestampVerify'));
const TimestampProof = React.lazy(() => import('./pages/TimestampProof'));
const PadesSeal = React.lazy(() => import('./pages/PadesSeal'));
const IdvReturn = React.lazy(() => import('./pages/IdvReturn'));
const AdminGoLive = React.lazy(() => import('./pages/AdminGoLive'));
const StatusPage = React.lazy(() => import('./pages/StatusPage'));
const AdminHederaControl = React.lazy(() => import('./pages/AdminHederaControl'));
const AdminTimestampCeremony = React.lazy(() => import('./pages/AdminTimestampCeremony'));
const AdminMockCeremony = React.lazy(() => import('./pages/AdminMockCeremony'));
const DocumentLibrary = React.lazy(() => import('./pages/DocumentLibrary'));
import {
  PricingPage, QuickSealDemo, QuickSealReceipt, MySeals, Dashboard, NotaryOnboarding, NotaryDashboard,
  RequestNotarization, VerifyDocument, CheckoutPage, CryptoCheckout, PaymentSuccess,
  PaymentCancel, NotaryVideoSession, AdminDashboard, AdminIntegrations, LivingIdentity,
  PublicChallenge, PublicVerify, NotaryDirectory, NotaryProfile, TrustLayerLanding,
  TrustGraph, AdminTrustLayer, AssetVault, HandoffAccept, TrustHub,
  FloridaLanding, FloridaNotaryOnboard, KBATest, FloridaWitnessAccept, FloridaCeremonyReadiness,
  NotaryFLJournal, AdminFLCompliance, AdminSubpoena, FLNotaryRecruitment, NotariesWanted, AdminFLRecruitment,
  AdminFLRonsp, FieldScanner, PublicScannerDemo, EmbedCeremony, DeveloperSDK,
  SDKKeys, MyDocuments, StateComparison, StateDetail, CompliancePublicSnapshot, ComplianceMatrix, VerifyWallet, SmartJournal,
  PartnerProgram, PartnerKit, AdminPartners, Academy, AcademyModule, AcademyExam, AdminAcademy,
  UserGuide, Marketplace, AdminCeremonyAnalytics, TrustBadges, TrustBadgeLanding,
  TransactionsPage, TransactionRoom, NotarizationCertificate, BlueprintCreator, SecuritySettings,
  SubscriptionPage, SubscriptionSuccess, NotaryJournal, DigitalSeal, CompliancePage,
  DeveloperPage, RONComplianceDashboard, TemplateLibrary, TemplateWizard, SmartContractsPage, MyAnchors, VerifyAnchor, CeremonyVaultPage, VerifyCeremonyVideo, OrganizationPage,
  MyDrafts, SharedDraftViewer, BulkNotarization, NotaryMarketplace, WhiteLabelPage,
  BookingCalendar, MyBookings, ReviewInsights, VerifyEmail, AIDocumentGenerator, AIDocumentSummarizer, VideoWitness,
  TemplateMarketplace,
  DocumentRemediation, BiometricPassportPage, AIConductorPage, EvidencePackagePage, TransactionTimeline,
  RemindersPage, ApprovalsPage, DocComparePage, BrandingPage, SSOLoginPage,
  Auth0Callback, OktaCallback, OnboardingPage, InvestorDeck, CeremonyDashboard,
  VerifyCertificate, EscrowDashboard, ANANDashboard, FraudIntelligencePage, AIIntelligenceHub,
  PublicAuditTrail, CeremonyReplay, MultiSignature, CertificateExpiration, TokenizedEscrow,
  PCVDashboard, PCVPublicVerify, PCVMarketing, NotaryIdCard, IdCardVerify, MagicLinkVerify, SignerPrep,
  BatchCertificates,
  ACNDashboard, ACNPublicVerify, SovereignID, SovereignVerify, BetaProgram,
  NotaryGuard, NotaryGuardVerify, ForgotPasswordPage, ResetPasswordPage, TrainingManual, AdminDataCleanup, ForcePasswordChange, Force2FASetup,
  FLRegistryLookup, PDFSplitter, NotFound, HederaAnalytics, AdminCampaignLinks,
  FraudShield, LitigationPackets, ContractorLanding, ContractorApp, AdminCoercionDrill, ParcelVerify, DeedJourney, CountyFeedRoadmap, PublicDeedProof, AdminLockouts, NotaryTagKit, UnlockAccount, VerifySeal,
  AdminSmsLog, RescheduleByLink, ForInvestors, InvestorDemo, Architecture, TrustCenter, VesselAircraftClosings, PracticeSession,
  Apostille, EscrowClientView, VerifyEscrowContract, EClosing, EarningsDashboard,
  RetentionDashboard, ConciergeWorkboard, TitleCompany, AdminAlertCenter, AdminSupportView, ClosingClientView,
  AdminSalesLeads, AdminRequests, AdminCspReports,
  AdminCompliancePolicies, LoiManager, LoiSign,
  DealsList, DealRoom, ChainOfTitle, DealSign, ClientDealView,
} from './lazyRoutes';

const PageLoader = () => (
  <div className="min-h-screen bg-[#0f1825] flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-2 border-coral-300 border-t-transparent rounded-full animate-spin" />
      <p className="text-gray-500 text-sm">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider>
    <div className="App">
      <AuthProvider>
        <SubscriptionProvider>
        <WebSocketProvider>
        <BrowserRouter>
          <ScrollToTop />
          <DemoRibbon />
          <GlobalSubheader />
          <EmailVerifyBanner />
          <ReviewerTour />
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* FOCUS MODE: parked (off-thesis) paths resolve to NotFound first.
                Reversible via REACT_APP_ENABLE_PARKED=true. See config/parkedFeatures.js */}
            {FOCUS_MODE && PARKED_PATHS.map((p) => (
              <Route key={`parked-${p}`} path={p} element={<NotFound />} />
            ))}
            <Route path="/" element={<NotaryLanding />} />
            <Route path="/old-home" element={<HomePage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/sso/login" element={<SSOLoginPage />} />
            <Route path="/auth/callback" element={<Auth0Callback />} />
            <Route path="/auth/okta/callback" element={<OktaCallback />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/demo" element={<QuickSealDemo />} />
            <Route path="/pades" element={<PadesSeal />} />
            <Route path="/intake" element={<IntakePortal />} />
            <Route path="/closing-rooms" element={<ClosingRooms />} />
            <Route path="/closing-rooms/join/:token" element={<ClosingRoomJoin />} />
            <Route path="/closing-rooms/:id" element={<ClosingRoom />} />
            <Route
              path="/federal-assets"
              element={
                <ProtectedRoute>
                  <FederalAssets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/federal/portfolio"
              element={
                <ProtectedRoute>
                  <FederalPortfolio />
                </ProtectedRoute>
              }
            />
            <Route path="/federal/verify" element={<FederalVerify />} />
            <Route path="/federal/verify/:ref" element={<FederalVerify />} />
            <Route path="/verify/timestamp" element={<TimestampVerify />} />
            <Route path="/verify/timestamp/:identifier" element={<TimestampVerify />} />
            <Route path="/proof/:identifier" element={<TimestampProof />} />
            <Route path="/status" element={<StatusPage />} />
            <Route path="/seal/:sealId" element={<QuickSealReceipt />} />
            <Route
              path="/my-seals"
              element={
                <ProtectedRoute>
                  <MySeals />
                </ProtectedRoute>
              }
            />
            <Route path="/verify" element={<PublicVerify />} />
            <Route path="/hedera-analytics" element={<HederaAnalytics />} />
            <Route path="/fraud-shield" element={<FraudShield />} />
            <Route path="/contractor" element={<ContractorLanding />} />
            <Route path="/loi/sign/:token" element={<LoiSign />} />
            <Route path="/provenance/:assetId" element={<ChainOfTitle />} />
            <Route path="/deals/sign/:token" element={<DealSign />} />
            <Route path="/deals/view/:token" element={<ClientDealView />} />
            <Route
              path="/deals"
              element={
                <ProtectedRoute>
                  <DealsList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/deals/:id"
              element={
                <ProtectedRoute>
                  <DealRoom />
                </ProtectedRoute>
              }
            />
            <Route
              path="/loi"
              element={
                <ProtectedRoute>
                  <LoiManager />
                </ProtectedRoute>
              }
            />
            <Route path="/escrow/view/:token" element={<EscrowClientView />} />
            <Route
              path="/force-password-change"
              element={
                <ProtectedRoute>
                  <ForcePasswordChange />
                </ProtectedRoute>
              }
            />
            <Route
              path="/force-2fa-setup"
              element={
                <ProtectedRoute>
                  <Force2FASetup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/compliance-policies"
              element={
                <ProtectedRoute>
                  <AdminCompliancePolicies />
                </ProtectedRoute>
              }
            />
            <Route
              path="/apostille"
              element={
                <ProtectedRoute>
                  <Apostille />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eclosing"
              element={
                <ProtectedRoute>
                  <EClosing />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notary/earnings"
              element={
                <ProtectedRoute>
                  <EarningsDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/title-company" element={<TitleCompany />} />
            <Route path="/closing/view/:token" element={<ClosingClientView />} />
            <Route
              path="/admin/retention"
              element={
                <ProtectedRoute>
                  <RetentionDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/concierge"
              element={
                <ProtectedRoute>
                  <ConciergeWorkboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/alerts"
              element={
                <ProtectedRoute>
                  <AdminAlertCenter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/support"
              element={
                <ProtectedRoute>
                  <AdminSupportView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contractor/app"
              element={
                <ProtectedRoute>
                  <ContractorApp />
                </ProtectedRoute>
              }
            />
            <Route
              path="/litigation-packets"
              element={
                <ProtectedRoute>
                  <LitigationPackets />
                </ProtectedRoute>
              }
            />
            <Route path="/verify/wallet" element={<VerifyWallet />} />
            {/* Legacy URL redirects (old shared/indexed links) */}
            <Route path="/contractor-escrow" element={<Navigate to="/contractor" replace />} />
            <Route path="/e-apostille" element={<Navigate to="/apostille" replace />} />
            <Route path="/state-compliance" element={<Navigate to="/compliance/states" replace />} />
            <Route
              path="/admin/coercion-drill"
              element={
                <ProtectedRoute>
                  <AdminCoercionDrill />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/lockouts"
              element={
                <ProtectedRoute>
                  <AdminLockouts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/sales-leads"
              element={
                <ProtectedRoute>
                  <AdminSalesLeads />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/requests"
              element={
                <ProtectedRoute>
                  <AdminRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/csp-reports"
              element={
                <ProtectedRoute>
                  <AdminCspReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/sms-log"
              element={
                <ProtectedRoute>
                  <AdminSmsLog />
                </ProtectedRoute>
              }
            />
            <Route path="/reschedule/:token" element={<RescheduleByLink />} />
            <Route
              path="/practice"
              element={
                <ProtectedRoute>
                  <PracticeSession />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notary/tag-kit"
              element={
                <ProtectedRoute>
                  <NotaryTagKit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parcel-verify"
              element={
                <ProtectedRoute>
                  <ParcelVerify />
                </ProtectedRoute>
              }
            />
            <Route
              path="/deed-journey/:bookingId"
              element={
                <ProtectedRoute>
                  <DeedJourney />
                </ProtectedRoute>
              }
            />
            <Route path="/deed-proof/:token" element={<PublicDeedProof />} />
            <Route path="/unlock-account" element={<UnlockAccount />} />
            <Route path="/verify-seal" element={<VerifySeal />} />
            <Route
              path="/admin/county-feeds"
              element={
                <ProtectedRoute>
                  <CountyFeedRoadmap />
                </ProtectedRoute>
              }
            />
            <Route path="/partners" element={<PartnerProgram />} />
            <Route path="/academy" element={<Academy />} />
            <Route path="/registry/fl" element={<FLRegistryLookup />} />
            <Route
              path="/tools/pdf-splitter"
              element={
                <ProtectedRoute>
                  <PDFSplitter />
                </ProtectedRoute>
              }
            />
            <Route path="/training/florida-manual" element={<TrainingManual />} />
            <Route path="/academy/module/:moduleId" element={<AcademyModule />} />
            <Route
              path="/academy/exam"
              element={
                <ProtectedRoute>
                  <AcademyExam />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/academy"
              element={
                <ProtectedRoute>
                  <AdminAcademy />
                </ProtectedRoute>
              }
            />
            <Route
              path="/partners/kit"
              element={
                <ProtectedRoute>
                  <PartnerKit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/partners"
              element={
                <ProtectedRoute>
                  <AdminPartners />
                </ProtectedRoute>
              }
            />
            <Route path="/verify/contract" element={<VerifyAnchor />} />
            <Route path="/verify/contract/:hash" element={<VerifyAnchor />} />
            <Route path="/verify/escrow" element={<VerifyEscrowContract />} />
            <Route path="/verify/escrow/:contractId" element={<VerifyEscrowContract />} />
            <Route path="/verify/recording" element={<VerifyCeremonyVideo />} />
            <Route path="/verify/recording/:hash" element={<VerifyCeremonyVideo />} />
            <Route path="/notaries" element={<NotaryDirectory />} />
            <Route path="/notaries/:stateCode" element={<NotaryDirectory />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/notary/:notaryId" element={<NotaryProfile />} />
            <Route path="/trustlayer" element={<TrustLayerLanding />} />
            <Route path="/investors" element={<ForInvestors />} />
            <Route path="/investor-demo" element={<InvestorDemo />} />
            <Route path="/architecture" element={<Architecture />} />
            <Route path="/security" element={<TrustCenter />} />
            <Route path="/vessel-aircraft" element={<VesselAircraftClosings />} />
            <Route path="/sovereign/verify/:sovereignId" element={<SovereignVerify />} />
            <Route
              path="/sovereign-id"
              element={
                <ProtectedRoute>
                  <SovereignID />
                </ProtectedRoute>
              }
            />
            <Route path="/trust-graph/:userId" element={<TrustGraph />} />
            <Route
              path="/admin/trustlayer"
              element={
                <ProtectedRoute>
                  <AdminTrustLayer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/asset-vault"
              element={
                <ProtectedRoute>
                  <AssetVault />
                </ProtectedRoute>
              }
            />
            <Route path="/handoff/:token" element={<HandoffAccept />} />
            <Route path="/florida" element={<FloridaLanding />} />
            <Route
              path="/notary/onboard/florida"
              element={
                <ProtectedRoute>
                  <FloridaNotaryOnboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/kba-test"
              element={
                <ProtectedRoute>
                  <KBATest />
                </ProtectedRoute>
              }
            />
            <Route
              path="/idv/return"
              element={
                <ProtectedRoute>
                  <IdvReturn />
                </ProtectedRoute>
              }
            />
            <Route path="/florida/witness/:token" element={<FloridaWitnessAccept />} />
            <Route
              path="/florida/ceremony-readiness"
              element={
                <ProtectedRoute>
                  <FloridaCeremonyReadiness />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notary/fl-journal"
              element={
                <ProtectedRoute>
                  <NotaryFLJournal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/fl-compliance"
              element={
                <ProtectedRoute>
                  <AdminFLCompliance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/subpoena"
              element={
                <ProtectedRoute>
                  <AdminSubpoena />
                </ProtectedRoute>
              }
            />
            <Route path="/florida/notaries" element={<FLNotaryRecruitment />} />
            <Route path="/notaries-wanted" element={<NotariesWanted />} />
            <Route path="/notaries-wanted/:stateCode" element={<NotariesWanted />} />
            <Route
              path="/admin/fl-recruitment"
              element={
                <ProtectedRoute>
                  <AdminFLRecruitment />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/fl-ronsp"
              element={
                <ProtectedRoute>
                  <AdminFLRonsp />
                </ProtectedRoute>
              }
            />
            <Route
              path="/scanner"
              element={
                <ProtectedRoute>
                  <FieldScanner />
                </ProtectedRoute>
              }
            />
            <Route path="/scanner/demo" element={<PublicScannerDemo />} />
            <Route path="/embed/ceremony/:token" element={<EmbedCeremony />} />
            <Route path="/developers/sdk" element={<DeveloperSDK />} />
            <Route
              path="/developers/sdk-keys"
              element={
                <ProtectedRoute>
                  <SDKKeys />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-documents"
              element={
                <ProtectedRoute>
                  <MyDocuments />
                </ProtectedRoute>
              }
            />
            <Route path="/compliance/states" element={<StateComparison />} />
            <Route path="/compliance/states/:code" element={<StateDetail />} />
            <Route path="/compliance/matrix" element={<ComplianceMatrix />} />
            <Route path="/compliance/snapshot/:token" element={<CompliancePublicSnapshot />} />
            <Route path="/docs" element={<UserGuide />} />
            <Route path="/help" element={<UserGuide />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute>
                  <AdminCeremonyAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trust-hub"
              element={
                <ProtectedRoute>
                  <TrustHub />
                </ProtectedRoute>
              }
            />
            <Route path="/trust-badge" element={<TrustBadgeLanding />} />
            <Route path="/verify-document" element={<VerifyDocument />} />
            <Route path="/verify-certificate" element={<VerifyCertificate />} />
            <Route path="/verify-certificate/:certHash" element={<VerifyCertificate />} />
            <Route path="/investor-deck" element={<InvestorDeck />} />
            <Route path="/audit-trail" element={<PublicAuditTrail />} />
            <Route
              path="/ceremony"
              element={
                <ProtectedRoute>
                  <CeremonyDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ceremony/:ceremonyId"
              element={
                <ProtectedRoute>
                  <CeremonyDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <OnboardingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/escrow"
              element={
                <ProtectedRoute>
                  <GatedRoute feature="escrow_intelligence" title="Escrow Intelligence" description="Smart contract-based escrow with AI-verified conditions. Requires Enterprise plan.">
                    <EscrowDashboard />
                  </GatedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/escrow/:escrowId"
              element={
                <ProtectedRoute>
                  <GatedRoute feature="escrow_intelligence" title="Escrow Intelligence" description="Smart contract-based escrow with AI-verified conditions. Requires Enterprise plan.">
                    <EscrowDashboard />
                  </GatedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/anan"
              element={
                <ProtectedRoute>
                  <GatedRoute feature="anan" title="Autonomous Notary Agent Network" description="Blind 2-of-3 AI consensus, SAN bond tracking, and agent reputation. Requires Enterprise plan.">
                    <ANANDashboard />
                  </GatedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/anan/:ceremonyId"
              element={
                <ProtectedRoute>
                  <GatedRoute feature="anan" title="Autonomous Notary Agent Network" description="Blind 2-of-3 AI consensus, SAN bond tracking, and agent reputation. Requires Enterprise plan.">
                    <ANANDashboard />
                  </GatedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/fraud-intelligence"
              element={
                <ProtectedRoute>
                  <GatedRoute feature="fraud_intelligence" title="Fraud Intelligence" description="AI-powered fraud detection and analytics dashboard. Requires Enterprise plan.">
                    <FraudIntelligencePage />
                  </GatedRoute>
                </ProtectedRoute>
              }
            />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/checkout/crypto" element={<CryptoCheckout />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/request-notarization"
              element={
                <ProtectedRoute>
                  <RequestNotarization />
                </ProtectedRoute>
              }
            />
            <Route
              path="/session/:requestId"
              element={
                <ProtectedRoute>
                  <NotaryVideoSession />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notary/onboarding"
              element={
                <ProtectedRoute>
                  <NotaryOnboarding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notary/dashboard"
              element={
                <ProtectedRoute>
                  <NotaryDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/data-cleanup"
              element={
                <ProtectedRoute>
                  <AdminDataCleanup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/hedera"
              element={
                <ProtectedRoute>
                  <AdminHederaControl />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/timestamp-ceremony"
              element={
                <ProtectedRoute>
                  <AdminTimestampCeremony />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/mock-ceremony"
              element={
                <ProtectedRoute>
                  <AdminMockCeremony />
                </ProtectedRoute>
              }
            />
            <Route
              path="/library"
              element={
                <ProtectedRoute>
                  <DocumentLibrary />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/integrations"
              element={
                <ProtectedRoute>
                  <AdminIntegrations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/go-live"
              element={
                <ProtectedRoute>
                  <AdminGoLive />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/campaign-links"
              element={
                <ProtectedRoute>
                  <AdminCampaignLinks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/identity"
              element={
                <ProtectedRoute>
                  <LivingIdentity />
                </ProtectedRoute>
              }
            />
            <Route path="/identity/challenge/:token" element={<PublicChallenge />} />
            <Route
              path="/badges"
              element={
                <ProtectedRoute>
                  <TrustBadges />
                </ProtectedRoute>
              }
            />
            <Route
              path="/transactions"
              element={
                <ProtectedRoute>
                  <TransactionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/transactions/:transactionId"
              element={
                <ProtectedRoute>
                  <TransactionRoom />
                </ProtectedRoute>
              }
            />
            <Route
              path="/certificate/:requestId"
              element={
                <ProtectedRoute>
                  <NotarizationCertificate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/blueprints/create"
              element={
                <ProtectedRoute>
                  <BlueprintCreator />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/security"
              element={
                <ProtectedRoute>
                  <SecuritySettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription"
              element={
                <ProtectedRoute>
                  <SubscriptionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/beta"
              element={
                <ProtectedRoute>
                  <BetaProgram />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notaryguard"
              element={
                <ProtectedRoute>
                  <NotaryGuard />
                </ProtectedRoute>
              }
            />
            <Route path="/notaryguard/verify" element={<NotaryGuardVerify />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route
              path="/subscription/success"
              element={
                <ProtectedRoute>
                  <SubscriptionSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/journal"
              element={
                <ProtectedRoute>
                  <SmartJournal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notary/journal"
              element={
                <ProtectedRoute>
                  <NotaryJournal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notary/seal"
              element={
                <ProtectedRoute>
                  <DigitalSeal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/compliance"
              element={
                <ProtectedRoute>
                  <CompliancePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/developers"
              element={
                <ProtectedRoute>
                  <DeveloperPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/ron-compliance"
              element={
                <ProtectedRoute>
                  <RONComplianceDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates"
              element={
                <ProtectedRoute>
                  <TemplateLibrary />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates/:templateId/fill"
              element={
                <ProtectedRoute>
                  <TemplateWizard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/smart-contracts"
              element={
                <ProtectedRoute>
                  <SmartContractsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-anchors"
              element={
                <ProtectedRoute>
                  <MyAnchors />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ceremony-vault"
              element={
                <ProtectedRoute>
                  <CeremonyVaultPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/organizations"
              element={
                <ProtectedRoute>
                  <GatedRoute feature="organization" title="Organization Management" description="Multi-seat team management with RBAC. Requires Enterprise plan.">
                    <OrganizationPage />
                  </GatedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-drafts"
              element={
                <ProtectedRoute>
                  <MyDrafts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/drafts/shared/:shareToken"
              element={
                <ProtectedRoute>
                  <SharedDraftViewer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bulk-notarization"
              element={
                <ProtectedRoute>
                  <GatedRoute feature="bulk_notarization" title="Bulk Notarization" description="Process multiple notarizations in batch. Requires Enterprise plan.">
                    <BulkNotarization />
                  </GatedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketplace"
              element={
                <ProtectedRoute>
                  <NotaryMarketplace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/white-label"
              element={
                <ProtectedRoute>
                  <GatedRoute feature="white_label" title="White Label Branding" description="Customize the platform with your own branding. Requires Enterprise plan.">
                    <WhiteLabelPage />
                  </GatedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/book/:notaryId"
              element={
                <ProtectedRoute>
                  <BookingCalendar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-bookings"
              element={
                <ProtectedRoute>
                  <MyBookings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reviews/insights"
              element={
                <ProtectedRoute>
                  <ReviewInsights />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-generator"
              element={
                <ProtectedRoute>
                  <GatedRoute feature="ai_generator" title="AI Document Generator" description="AI-powered legal document generation. Requires Professional plan.">
                    <AIDocumentGenerator />
                  </GatedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/template-marketplace"
              element={
                <ProtectedRoute>
                  <TemplateMarketplace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-summarizer"
              element={
                <ProtectedRoute>
                  <GatedRoute feature="ai_summarizer" title="AI Document Summarizer" description="AI-powered legal document summarization. Requires Professional plan.">
                    <AIDocumentSummarizer />
                  </GatedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/video-witness"
              element={
                <ProtectedRoute>
                  <GatedRoute feature="video_witness" title="Video Witness" description="Remote video notarization sessions. Requires Professional plan.">
                    <VideoWitness />
                  </GatedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/document-remediation"
              element={
                <ProtectedRoute>
                  <GatedRoute feature="doc_remediation" title="Document Remediation" description="AI-powered document remediation and compliance fixes. Requires Professional plan.">
                    <DocumentRemediation />
                  </GatedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/biometric-passport"
              element={
                <ProtectedRoute>
                  <GatedRoute feature="biometric_passport" title="Biometric Passport" description="Advanced biometric identity verification. Requires Professional plan.">
                    <BiometricPassportPage />
                  </GatedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/conductor/:transactionId"
              element={
                <ProtectedRoute>
                  <AIConductorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/evidence-package/:transactionId"
              element={
                <ProtectedRoute>
                  <EvidencePackagePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/timeline/:transactionId"
              element={
                <ProtectedRoute>
                  <TransactionTimeline />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reminders"
              element={
                <ProtectedRoute>
                  <RemindersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/approvals"
              element={
                <ProtectedRoute>
                  <ApprovalsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/doc-compare"
              element={
                <ProtectedRoute>
                  <DocComparePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/branding"
              element={
                <ProtectedRoute>
                  <BrandingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-intelligence"
              element={
                <ProtectedRoute>
                  <GatedRoute feature="ai_intelligence_hub" title="AI Intelligence Hub" description="5 AI-powered features: Risk Scoring, Summarizer, Notary Matching, Fraud Detection, Voice Auth. Requires Enterprise plan.">
                    <AIIntelligenceHub />
                  </GatedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/ceremony-replay/:ceremonyId"
              element={
                <ProtectedRoute>
                  <GatedRoute feature="ceremony_replay" title="Ceremony Replay" description="Step-by-step animated replay of past ceremony agent pipelines. Requires Professional plan.">
                    <CeremonyReplay />
                  </GatedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/multi-signature"
              element={
                <ProtectedRoute>
                  <GatedRoute feature="multi_signature" title="Multi-Signature Ceremonies" description="Support for 2-10 signers per ceremony with individual biometric verification. Requires Enterprise plan.">
                    <MultiSignature />
                  </GatedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/certificate-expiration"
              element={
                <ProtectedRoute>
                  <GatedRoute feature="certificate_expiration" title="Certificate Expiration" description="Set validity periods and track expiring certificates. Requires Professional plan.">
                    <CertificateExpiration />
                  </GatedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tokenized-escrow"
              element={
                <ProtectedRoute>
                  <GatedRoute feature="hts_tokens" title="HTS Tokenized Escrow" description="On-chain Hedera Token Service for tokenized escrow agreements. Requires Enterprise plan.">
                    <TokenizedEscrow />
                  </GatedRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pcv"
              element={
                <ProtectedRoute>
                  <PCVDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/pcv/verify/:packetId" element={<PCVPublicVerify />} />
            <Route path="/id/:cardId" element={<IdCardVerify />} />
            <Route path="/magic/:token" element={<MagicLinkVerify />} />
            <Route path="/prep/:token" element={<SignerPrep />} />
            <Route
              path="/notary/id-card"
              element={
                <ProtectedRoute>
                  <NotaryIdCard />
                </ProtectedRoute>
              }
            />
            <Route path="/pcv/marketing" element={<PCVMarketing />} />
            <Route
              path="/admin/batch-certificates"
              element={
                <ProtectedRoute>
                  <BatchCertificates />
                </ProtectedRoute>
              }
            />
            <Route
              path="/acn"
              element={
                <ProtectedRoute>
                  <ACNDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/acn/verify/:packetId" element={<ACNPublicVerify />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          <PlatformFooter />
        </BrowserRouter>
        </WebSocketProvider>
        </SubscriptionProvider>
        <Toaster />
        <SonnerToaster position="top-right" richColors closeButton />
      </AuthProvider>
    </div>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;